import { defineStore } from 'pinia'

import { useBookmarkFoldersStore } from 'src/stores/bookmark_folders.js'
import { useInterfaceStore } from 'src/stores/interface.js'
import { useStreamingStore } from 'src/stores/streaming.js'
import { useUsersStore } from 'src/stores/users.js'

import {
  fetchEmojiReactions,
  fetchFavoritedByUsers,
  fetchRebloggedByUsers,
  fetchStatus,
  fetchStatusSource,
} from 'src/api/public.js'
import { fetchTimeline } from 'src/api/timelines.js'
import {
  bookmarkStatus,
  deleteStatus,
  favorite,
  muteConversation,
  pinOwnStatus,
  reactWithEmoji,
  retweet,
  translateStatus,
  unbookmarkStatus,
  unfavorite,
  unmuteConversation,
  unpinOwnStatus,
  unreactWithEmoji,
  unretweet,
} from 'src/api/user.js'

export const defaultState = () => ({
  allStatuses: new Map(),
  statusesPerUser: new Map(),
  timestamps: new WeakMap(),
  scrobblesNextFetch: {},
  conversations: new Map(),
  favorites: new Set(),
  socket: null,
  favs: new Map(),
  repeats: new Map(),
  quoteAuthors: new Map(),
})

// Share hover requests across popovers for the same post/kind, not their UI state.
const participantRequests = new WeakMap()
const loadParticipants = (store, key, load) => {
  if (!participantRequests.has(store)) participantRequests.set(store, new Map())
  const requests = participantRequests.get(store)
  if (!requests.has(key)) {
    const request = load().finally(() => requests.delete(key))
    requests.set(key, request)
  }
  return requests.get(key)
}

// Quote refreshes replace IDs; appends must read the cursor after any refresh.
// Serialize conflicting operations per status, coalescing adjacent identical ones.
const quoteRequests = new WeakMap()
const queueQuoteAuthors = (store, id, more, load) => {
  const statuses = store.allStatuses
  if (!quoteRequests.has(statuses)) quoteRequests.set(statuses, new Map())
  const requests = quoteRequests.get(statuses)
  const previous = requests.get(id)
  if (previous?.more === more) return previous.request
  // A queued read must not acquire the new account's credentials after reset.
  const run = () => (store.allStatuses === statuses ? load() : false)
  const request = (
    previous
      ? previous.request.catch(() => undefined).then(run)
      : Promise.resolve(run())
  ).finally(() => {
    if (requests.get(id)?.request === request) requests.delete(id)
  })
  requests.set(id, { more, request })
  return request
}

// running translation requests, by post and language
const translating = new Map()

// clicks on a post (favorite, retweet, emoji), by post and kind: how many
// so far, and how many still wait for the server
const interactions = new Map()
const keyOf = (id, name) => `${id} ${name}`
const interaction = (id, name) => {
  const key = keyOf(id, name)
  if (!interactions.has(key)) interactions.set(key, { started: 0, pending: 0 })
  return interactions.get(key)
}
const clicksOf = (id, name) => interactions.get(keyOf(id, name))?.started ?? 0
const waiting = (id, name) => interactions.get(keyOf(id, name))?.pending > 0
// a "who did it" list asked for before a click, or while one waits, does
// not know the click: it must not undo what the click shows
const clickedSince = (id, name, clicks) =>
  clicksOf(id, name) !== clicks || waiting(id, name)
// what a click changes on its post: another copy of the post (the answer
// to another click, a poll) keeps them while the click waits
const OWN_FIELDS = {
  favorite: ['favorited', 'fave_num'],
  retweet: ['repeated', 'repeat_num'],
  emoji: ['emoji_reactions'],
}
// who favorited or repeated: the count and your own state follow the list,
// unless a click came since it was asked for (current false); then the
// list follows your state
const takePeople = (status, users, current, [flag, count]) => {
  const me = useUsersStore().currentUser?.id
  if (current) {
    status[count] = users.size
    status[flag] = users.has(me)
  } else if (me && status[flag]) {
    users.add(me)
  } else {
    users.delete(me)
  }
}

export const useStatusesStore = defineStore('statuses', {
  state: defaultState,
  getters: {
    // the conversation a post shows (a repeat: the repeated post's), read
    // from the store's own post: a copy can have an old thread
    conversationIdOf() {
      return (statusId) => {
        const status = this.allStatuses.get(statusId)
        const shown = status?.retweeted_status
          ? this.allStatuses.get(status.retweeted_status.id)
          : status
        return shown?.statusnet_conversation_id
      }
    },
  },
  actions: {
    // Init
    attachSocket() {
      const et = new EventTarget()
      const handleUpdate = ({ data, timestamp }) =>
        this.addNewStatuses({ statuses: data, timestamp })
      const handleDelete = ({ data }) =>
        data.forEach((id) => this.setDeleted(id))

      const socket = {
        name: 'statuses',
        et,
        handlers: {
          handleUpdate,
          handleDelete,
        },
      }

      et.addEventListener('update', handleUpdate)
      et.addEventListener('status.update', handleUpdate)
      et.addEventListener('delete', handleDelete)

      useStreamingStore().addSubscriber(socket)
      this.socket = socket
    },
    resetStatuses() {
      participantRequests.delete(this)
      interactions.clear()
      const emptyState = defaultState()
      Object.entries(emptyState).forEach(([key, value]) => {
        if (key === 'socket') return
        this[key] = value
      })
    },

    addNewStatuses({ statuses, timestamp }) {
      // Sanity check
      if (!Array.isArray(statuses)) {
        throw new TypeError("Statuses aren't an array!")
      }

      // addStatus should always return "main" status,
      // not "sub-status" i.e. retweeted/quoted/liked status
      // in case of likes (which are not statuses) it should return null
      const addStatus = (data) => {
        const [status] = this.mergeOrAdd(this.allStatuses, data, timestamp)
        let userSet = this.statusesPerUser.get(status.user.id)
        if (userSet === undefined) {
          userSet = new Set()
          this.statusesPerUser.set(status.user.id, userSet)
        }
        userSet.add(status.id)

        // Add to conversation
        const conversations = this.conversations
        const conversationId = this.threadOf(status)
        status.statusnet_conversation_id = conversationId

        if (conversations.has(conversationId)) {
          conversations.get(conversationId).add(status.id)
        } else {
          conversations.set(conversationId, new Set([status.id]))
        }
        // the posts that took this one for their thread's start (they came
        // first) join its thread
        const below = `thread:${status.id}`
        if (below !== conversationId && conversations.has(below)) {
          for (const id of conversations.get(below)) {
            this.allStatuses.get(id).statusnet_conversation_id = conversationId
            conversations.get(conversationId).add(id)
          }
          conversations.delete(below)
        }

        // Work on quote
        if (status.quote) {
          status.quote = addStatus(status.quote)
        }

        return status
      }

      const processors = {
        status: (status) => {
          return addStatus(status)
        },
        edit: (status) => {
          return addStatus(status)
        },
        retweet: (status) => {
          // the store's post, as for a quote (its thread is known there)
          if (status.retweeted_status) {
            status.retweeted_status = addStatus(status.retweeted_status)
          }
          return addStatus(status)
        },
        default: (unknown) => {
          console.warn('unknown status type', unknown)
          return null
        },
      }

      return statuses.map((status) => {
        const type = status.type
        const processor = processors[type] ?? processors.default
        return processor(status)
      })
    },
    // A post's conversation. Pleroma gives its id; Mastodon gives none, so
    // a thread is known by the first of its posts that is loaded: a
    // reply is in its parent's thread, or in "thread:<parent id>" until
    // the parent comes (see addNewStatuses)
    threadOf(status) {
      if (status.statusnet_conversation_id != null) {
        return status.statusnet_conversation_id
      }
      const parentId = status.in_reply_to_status_id
      return (
        this.allStatuses.get(parentId)?.statusnet_conversation_id ??
        `thread:${parentId ?? status.id}`
      )
    },
    mergeOrAdd(map, status, timestamp) {
      const existing = map.get(status.id) ?? {}
      const oldTimestamp = this.timestamps.get(existing)

      const { user: unused0, ...old } = existing
      const { user: statusUser, ...neu } = status

      const [user] = useUsersStore().addNewUsers({
        data: statusUser,
        timestamp,
      })

      const { in_reply_to_user_id } = status
      if (in_reply_to_user_id) {
        useUsersStore().fetchUserIfMissing({ id: in_reply_to_user_id })
      }

      existing.user = user // reactive update in case we return old

      // implicit: if oldTimestamp is undefined this will still be false
      if (oldTimestamp > timestamp) return [existing, false] // not overwriting old data with new

      const newStatus = {
        ...old,
        ...Object.fromEntries(
          Object.entries(neu).filter(([, v]) => v !== undefined),
        ),
        user,
      }
      for (const [name, fields] of Object.entries(OWN_FIELDS)) {
        if (!waiting(newStatus.id, name)) continue
        for (const field of fields) {
          if (field in old) newStatus[field] = old[field]
        }
      }
      // a translation is of the text it was made from
      if (
        old.translation &&
        (neu.raw_html !== old.raw_html || neu.edited_at !== old.edited_at)
      ) {
        newStatus.translation = null
      }

      map.set(newStatus.id, newStatus)

      this.timestamps.set(newStatus, timestamp)

      return [map.get(newStatus.id), true]
    },

    // Fetches
    fetchStatus(id) {
      return fetchStatus({
        id,
      }).then(({ data: status, timestamp }) =>
        this.addNewStatuses({ statuses: [status], timestamp }),
      )
    },
    fetchStatusSource(id) {
      return fetchStatusSource({
        id,
      }).then(({ data }) => data)
    },
    fetchEmojiReactions(id, { listOnly = false } = {}) {
      if (listOnly)
        return loadParticipants(this, keyOf(id, 'emoji'), () =>
          this.fetchEmojiReactionsList(id, true),
        )
      return this.fetchEmojiReactionsList(id)
    },
    fetchEmojiReactionsList(id, listOnly = false) {
      const statuses = this.allStatuses
      const clicks = clicksOf(id, 'emoji')
      return fetchEmojiReactions({
        id,
      }).then(({ data, timestamp }) => {
        if (this.allStatuses !== statuses || clickedSince(id, 'emoji', clicks))
          return false
        const reactions = data.map((reaction) => {
          const users = useUsersStore().addNewUsers({
            timestamp,
            data: reaction.accounts,
          })

          // Backend inconsistency - status data only has ids (account_ids)
          // but reactions data has full info (accounts)
          return {
            ...reaction,
            accounts: users,
            account_ids: users.map(({ id }) => id),
          }
        })

        if (listOnly) {
          // Hover data hydrates participants, not counts or your own reaction.
          const status = this.allStatuses.get(id)
          status.emoji_reactions = status.emoji_reactions.map((reaction) => {
            const people = reactions.find(
              (r) => r.name === reaction.name && r.url === reaction.url,
            )
            return people
              ? {
                  ...reaction,
                  accounts: people.accounts,
                  account_ids: people.account_ids,
                }
              : reaction
          })
        } else {
          this.addEmojiReactionsBy(id, reactions)
        }
      })
    },
    fetchFavs(id, { listOnly = false } = {}) {
      if (listOnly)
        return loadParticipants(this, keyOf(id, 'favorite'), () =>
          this.fetchFavsList(id, true),
        )
      return this.fetchFavsList(id)
    },
    fetchFavsList(id, listOnly = false) {
      const statuses = this.allStatuses
      const clicks = clicksOf(id, 'favorite')
      return fetchFavoritedByUsers({
        id,
      }).then((result) => {
        if (this.allStatuses !== statuses) return false
        const users = useUsersStore().addNewUsers(result)
        return this.addFavs(
          id,
          new Set(users.map(({ id }) => id)),
          !clickedSince(id, 'favorite', clicks),
          listOnly,
        )
      })
    },
    fetchRepeats(id, { listOnly = false } = {}) {
      if (listOnly)
        return loadParticipants(this, keyOf(id, 'retweet'), () =>
          this.fetchRepeatsList(id, true),
        )
      return this.fetchRepeatsList(id)
    },
    fetchRepeatsList(id, listOnly = false) {
      const statuses = this.allStatuses
      const clicks = clicksOf(id, 'retweet')
      return fetchRebloggedByUsers({
        id,
      }).then((result) => {
        if (this.allStatuses !== statuses) return false
        const users = useUsersStore().addNewUsers(result)
        return this.addRepeats(
          id,
          new Set(users.map(({ id }) => id)),
          !clickedSince(id, 'retweet', clicks),
          listOnly,
        )
      })
    },
    fetchQuoteAuthors(id, { more = false } = {}) {
      const statuses = this.allStatuses
      return queueQuoteAuthors(this, id, more, async () => {
        const previous = this.quoteAuthors.get(id)
        const maxId = more ? previous?.pagination?.maxId : undefined
        if (more && !maxId) return
        const { data, timestamp, pagination } = await fetchTimeline({
          timeline: 'quotes',
          statusId: id,
          maxId,
        })
        if (this.allStatuses !== statuses) return false
        const users = useUsersStore().addNewUsers({
          data: data.map(({ user }) => user),
          timestamp,
        })
        this.quoteAuthors.set(id, {
          ids: new Set([
            ...(more ? previous.ids : []),
            ...users.map(({ id }) => id),
          ]),
          // Stop empty or non-advancing pages, even if the server repeats its link.
          pagination:
            data.length && pagination?.maxId !== maxId ? pagination : {},
        })
      })
    },
    fetchFavsAndRepeats(id) {
      return Promise.all([this.fetchFavs(id), this.fetchRepeats(id)])
    },

    // Updates
    // the counts from polling can be off: the list is newer
    addRepeats(id, users, current = true, listOnly = false) {
      if (!listOnly || !current)
        takePeople(this.allStatuses.get(id), users, current, OWN_FIELDS.retweet)
      this.repeats.set(id, users)
    },
    addFavs(id, users, current = true, listOnly = false) {
      if (!listOnly || !current)
        takePeople(
          this.allStatuses.get(id),
          users,
          current,
          OWN_FIELDS.favorite,
        )
      this.favs.set(id, users)
    },
    addEmojiReactionsBy(id, emojiReactions) {
      const status = this.allStatuses.get(id)
      status.emoji_reactions = emojiReactions
    },

    // Actions
    requestInteract({ name, id, optimisticCall, apiCall, argument, value }) {
      const oldValue = !value // Assumption

      const apiArgs = (() => {
        switch (name) {
          case 'emoji':
            return { emoji: argument }
          case 'bookmark':
            return { folder_id: argument }
          case 'retweet':
            return argument ? { visibility: argument } : {}
          default:
            return {}
        }
      })()

      // Optimistic
      optimisticCall(id, value, argument)
      const clicks = interaction(id, name)
      const click = ++clicks.started
      clicks.pending++
      // the answer to the last click applies; an older one that comes
      // later is out of date
      let answered = false
      const answer = () => {
        if (!answered) clicks.pending--
        answered = true
      }

      return apiCall({
        id,
        ...apiArgs,
      })
        .then(({ data: status, timestamp }) => {
          if (click === clicks.started) answer()
          this.addNewStatuses({
            statuses: [status],
            timestamp,
          })
          answer()
        })
        .catch((error) => {
          answer()
          optimisticCall(id, oldValue, argument)

          console.error('Interact Error', error)
          useInterfaceStore().pushGlobalNotice({
            level: 'error',
            messageKey: 'status.interact_error',
            messageArgs: { error },
            timeout: 5000,
          })
        })
    },

    /// Favorite
    favorite(id) {
      return this.requestInteract({
        name: 'favorite',
        id,
        apiCall: favorite,
        optimisticCall: this.setFavorited,
        value: true,
      })
    },
    unfavorite(id) {
      return this.requestInteract({
        name: 'favorite',
        id,
        apiCall: unfavorite,
        optimisticCall: this.setFavorited,
        value: false,
      })
    },
    setFavorited(id, value) {
      const newStatus = this.allStatuses.get(id)

      if (newStatus.favorited !== value) {
        if (value) {
          newStatus.fave_num++
        } else {
          newStatus.fave_num--
        }
      }

      newStatus.favorited = value
    },

    /// Reprööt
    // visibility: optional, the repeat's own scope
    // a translation of a post, kept on the post until hidden; a second
    // call while one runs (a double click) waits for the same request
    translate(id, lang) {
      const key = `${id} ${lang}`
      if (!translating.has(key)) {
        const request = translateStatus({
          id,
          lang,
        })
          .then(({ data }) => {
            const status = this.allStatuses.get(id)
            if (status) status.translation = { ...data, lang }
            return data
          })
          .finally(() => translating.delete(key))
        translating.set(key, request)
      }
      return translating.get(key)
    },
    clearTranslation(id) {
      const status = this.allStatuses.get(id)
      if (status) status.translation = null
    },
    retweet(id, visibility) {
      return this.requestInteract({
        name: 'retweet',
        id,
        apiCall: retweet,
        optimisticCall: this.setRetweeted,
        argument: visibility,
        value: true,
      })
    },
    unretweet(id) {
      return this.requestInteract({
        name: 'retweet',
        id,
        apiCall: unretweet,
        optimisticCall: this.setRetweeted,
        value: false,
      })
    },
    setRetweeted(id, value) {
      const newStatus = this.allStatuses.get(id)

      if (newStatus.repeated !== value) {
        if (value) {
          newStatus.repeat_num++
        } else {
          newStatus.repeat_num--
        }
      }

      newStatus.repeated = value
    },

    // React
    reactWithEmoji(id, emoji) {
      return this.requestInteract({
        name: 'emoji',
        id,
        apiCall: reactWithEmoji,
        optimisticCall: this.setOwnReaction,
        argument: emoji,
        value: true,
      })
    },
    unreactWithEmoji(id, emoji) {
      return this.requestInteract({
        name: 'emoji',
        id,
        apiCall: unreactWithEmoji,
        optimisticCall: this.setOwnReaction,
        argument: emoji,
        value: false,
      })
    },
    setOwnReaction(id, value, emoji) {
      const currentUser = useUsersStore().currentUser
      const status = this.allStatuses.get(id)
      const reactionIndex = status.emoji_reactions.findIndex(
        (react) => react.name === emoji,
      )
      const reactionPresent = reactionIndex >= 0
      if (!value && !reactionPresent) return

      const reaction = status.emoji_reactions[reactionIndex] || {
        name: emoji,
        count: 0,
        accounts: [],
        account_ids: [],
      }

      const count = value ? reaction.count + 1 : reaction.count - 1

      // Pleroma sends account_ids and no accounts (the people come from a
      // separate request, see EmojiReactions): accounts only where present
      const myId = currentUser?.id
      const others = (
        reaction.account_ids ??
        reaction.accounts?.filter(Boolean).map(({ id }) => id) ??
        []
      ).filter((id) => id !== myId)
      const account_ids = value && myId ? [...others, myId] : others

      const newReaction = {
        ...reaction,
        count,
        me: value,
        account_ids,
      }
      if (reaction.accounts) {
        const people = reaction.accounts.filter((acc) => acc && acc.id !== myId)
        newReaction.accounts =
          value && currentUser ? [...people, currentUser] : people
      }

      if (reactionPresent && count > 0) {
        status.emoji_reactions[reactionIndex] = newReaction
      } else if (count === 0) {
        status.emoji_reactions = status.emoji_reactions.filter(
          (r) => r.name !== emoji,
        )
      } else {
        status.emoji_reactions.push(newReaction)
      }
    },

    /// Bookmark
    bookmark(id, bookmark_folder_id) {
      // the feed menu shows bookmarks from now on
      useBookmarkFoldersStore().noteBookmark()
      return this.requestInteract({
        name: 'bookmark',
        id,
        apiCall: bookmarkStatus,
        optimisticCall: this.setBookmarked,
        argument: bookmark_folder_id,
        value: true,
      })
    },
    unbookmark(id) {
      return this.requestInteract({
        name: 'bookmark',
        id,
        apiCall: unbookmarkStatus,
        optimisticCall: this.setBookmarked,
        value: false,
      })
    },
    setBookmarked(id, value, bookmark_folder_id) {
      const status = this.allStatuses.get(id)
      status.bookmarked = value

      // When unbookmarking we don't specify folder so we wanna keep
      // reference to the folder even when setting bookmarked to false
      // the proper reference will be updated when api call resolves
      if (bookmark_folder_id) {
        status.bookmark_folder_id = value ? bookmark_folder_id : null
      }
    },

    /// Mute
    muteConversation(id) {
      return this.requestInteract({
        name: 'mute',
        id,
        apiCall: muteConversation,
        optimisticCall: this.setMutedStatus,
        value: true,
      })
    },
    unmuteConversation(id) {
      return this.requestInteract({
        name: 'mute',
        id,
        apiCall: unmuteConversation,
        optimisticCall: this.setMutedStatus,
        value: false,
      })
    },
    setMutedStatus(id, value) {
      // Setting thread_muted flag on all other known statuses
      // belonging to same conversation
      const newStatus = this.allStatuses.get(id)
      newStatus.thread_muted = value

      if (newStatus.thread_muted !== undefined) {
        this.conversations
          .get(newStatus.statusnet_conversation_id)
          .forEach((statusId) => {
            this.allStatuses.get(statusId).thread_muted = value
          })
      }
    },

    /// Pin
    pinStatus(id) {
      return this.requestInteract({
        name: 'pin',
        id,
        apiCall: pinOwnStatus,
        optimisticCall: () => {
          /* no-op */
        },
        value: true,
      })
    },
    unpinStatus(id) {
      return this.requestInteract({
        name: 'pin',
        id,
        apiCall: unpinOwnStatus,
        optimisticCall: () => {
          /* no-op */
        },
        value: false,
      })
    },

    /// Delete
    deleteStatus(id) {
      return deleteStatus({
        id,
      })
        .then(() => {
          this.setDeleted(id)
        })
        .catch((e) => {
          useInterfaceStore().pushGlobalNotice({
            level: 'error',
            messageKey: 'status.delete_error',
            messageArgs: [e.message],
            timeout: 5000,
          })
        })
    },
    setDeleted(id) {
      const newStatus = this.allStatuses.get(id)
      if (newStatus) newStatus.deleted = true
    },

    // For when blocking a user
    wipeUserStatuses(userId) {
      const removed = this.statusesPerUser.get(userId) ?? new Set()
      removed.forEach((statusId) => {
        const status = this.allStatuses.get(statusId)
        this.allStatuses.delete(statusId)
        const conversationSet = this.conversations.get(
          status.statusnet_conversation_id,
        )
        conversationSet.delete(statusId)
        if (conversationSet.size === 0) {
          this.conversations.delete(status.statusnet_conversation_id)
        }
      })
      this.statusesPerUser.delete(userId)
      return removed
    },
  },
})
